import { Component, ChangeDetectionStrategy } from '@angular/core';
import { trigger, transition, style, animate, query, stagger, state } from '@angular/animations';
import { NgxSpinnerService } from 'ngx-spinner';
import { AnstallningService } from './anstallning.service';
import { FileService } from '../../file/file.service';
import { AttestService } from '../../attest/attest.service';
import { saveAs } from 'file-saver-es';
import { FiliDb } from '../../interfaces/filiDb.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { fadeInAnimation } from '../../_animations/fade-in.animation';
import { Anstallning } from '../../interfaces/anstallning.interface';
import { SessionService } from '../../session/session.service';
import { Beslut } from '../../interfaces/beslut.interface';

@Component({
    selector: 'app-anstallning',
    templateUrl: './anstallning.component.html',
    styleUrls: ['./anstallning.component.scss'],
    animations: [
        fadeInAnimation,
        trigger('listAnimation', [
            transition('* => *', [
                query(':enter', [
                    style({ opacity: 0, transform: 'scale(.5) perspective(200px) rotateX(90deg)' }),
                    stagger(100, [
                        animate('300ms 100ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
                    ])
                ])
            ])
        ]),
        trigger('downloadButtonAnimation', [
            state('*', style({ opacity: 0, transform: 'scale(1.25)' })),
            state('active', style({ opacity: 1 })),
            transition('inactive => active', animate('300ms ease-out'))
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AnstallningComponent {

  constructor(
    protected service: AnstallningService,
    protected spinner: NgxSpinnerService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected sessionService: SessionService,
    protected fileService: FileService,
    protected attestService: AttestService) {
    this.loadAnstallning(this.route.snapshot.paramMap.get('id'));
  }

  private readonly anstallningId: string;

  public get isLoaded(): boolean { return !!this.anstallning };
  public get role(): string {
    return this.sessionService.activeInstance.roleType;
  };

  public orderName: string = 'datum';
  public order: string[] = [ 'datum' ];
  public reverse: boolean = true;
  public isConfidential: boolean;

  public anstallning: Anstallning;

  public get selectedBesluts(): Beslut[] { return this.anstallning.lonegarantiBeslut.filter(b => b.isSelected && b.fil); }
  public get selectedFiles(): FiliDb[] { return this.selectedBesluts.map(b => b.fil).concat(this.anstallning.bilagor.filter(b => b.isSelected)); }
  public get bilagors(): FiliDb[] { return this.anstallning.bilagor.filter(b => b.filAgare.typ === 'anst' || b.filAgare.typ === 'upps'); }
  public get harBeslutKvarAttSignera(): boolean { return !!this.anstallning.lonegarantiBeslut.find(b => b.beslutTyp.typ !== 'b2' && b.fil && b.fil.filtillagg.toLowerCase() === '.pdf' && !b.fil.signatur); };
  public get harBilagorKvarAttSignera(): boolean { return !!this.bilagors.find(f => f.filtillagg.toLowerCase() === '.pdf' && !f.signatur); };

  private loadAnstallning(id: string) {

    const callback = () => {
      setTimeout(() => {
        if (!this.anstallning) {
          this.spinner.show();
        }
      }, 300);

      this.service.getAnstallning(this.sessionService.activeInstance.id, id).subscribe(
        result => {
          this.spinner.hide();
          this.anstallning = result;
          this.isConfidential = this.anstallning.arbetstagare.fornamn.indexOf('SEKRETESS') !== -1;
        });
    };

    if (!this.sessionService.hasSession) {
      this.router.navigate(['/login']);
      return;
    }

    if (!this.sessionService.hasActiveInstance) {
      this.sessionService.selectInstance(callback);
      return;
    }

    callback();
  }

  public reloadAnstallning() {
    this.spinner.show();
    this.loadAnstallning(this.anstallning.id);
  }

  public get instanceName() { return this.sessionService.activeInstance.name; }

  public setOrder(name: string, order: string[]) {
    if (this.orderName === name) {
      this.reverse = !this.reverse;
    }

    this.orderName = name;
    this.order = order.slice();
  }

  public getSum(items: object[], property: string): number {
    let sum = 0;
    for (let i = 0; i < items.length; i++) {
      sum += +items[i][property];
    }

    return sum;
  }

  public addS(str: string): string {
    return str + (str.slice(-1) === 's' ? '' : 's');
  }

  public attestManyBeslut() {
    this.spinner.show();
    const selectedBeslut: string[] = [];

    for (let n = 0; n < this.anstallning.lonegarantiBeslut.length; n++) {
      const current = this.anstallning.lonegarantiBeslut[n];
      if (current.isSelected)
        selectedBeslut.push(current.id);
    }

    this.attestService.attestManyAnstallningBeslut(this.sessionService.activeInstance.id, this.anstallningId, selectedBeslut).subscribe(
      () => {
        this.reloadAnstallning();
      });
  }

  public getFile(file: FiliDb) {

    this.spinner.show();

    this.fileService.getFile(this.sessionService.activeInstance.id, file.id).subscribe(
      response => {
        this.spinner.hide();
        saveAs(new Blob([response.body]), file.filnamn + file.filtillagg);
      });
  }
}
